// Layout shell con sidebar collapsible (desktop) + bottom-tabs (mobile).
// Identidad "bini" — charcoal warm + acento dorado champagne.

import { useState, type ReactElement, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from './ui/ThemeToggle';
import { BiniLogo } from './BiniLogo';
import { cn } from '../lib/cn';

interface Props {
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  exact?: boolean;
  icon: ReactElement;
}

const ICON = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
      <circle cx="8" cy="14" r="0.8" fill="currentColor" />
      <circle cx="12" cy="14" r="0.8" fill="currentColor" />
      <circle cx="16" cy="14" r="0.8" fill="currentColor" />
    </svg>
  ),
  trades: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18l5-5 4 3 8-8" />
      <path d="M14 8h6v6" />
    </svg>
  ),
  news: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9c0-1.1.9-2 2-2h2" />
      <path d="M18 14h-8M15 18h-5M10 6h8v4h-8z" />
    </svg>
  ),
  portfolio: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L19 6v7c0 4.5-3.5 8.5-7 9-3.5-.5-7-4.5-7-9V6l7-4z" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  ),
  payouts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M14.5 9.3c-.5-.8-1.5-1.3-2.5-1.3-1.4 0-2.5.9-2.5 2s1.1 2 2.5 2 2.5.9 2.5 2-1.1 2-2.5 2c-1 0-2-.5-2.5-1.3" />
    </svg>
  ),
  accounts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="14" rx="2" />
      <path d="M2 11h20" />
      <path d="M7 16h3" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L4 5v6.5C4 16.5 7.4 20.7 12 22c4.6-1.3 8-5.5 8-10.5V5l-8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  bitacora: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M9 7h7M9 11h5" />
    </svg>
  ),
  estrategia: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M16 8l-2.5 6L8 16l2.5-6L16 8z" />
    </svg>
  ),
  coin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M14.5 9a2.5 2.5 0 0 0-2.5-1.5c-1.4 0-2.5.9-2.5 2s1.1 1.7 2.5 2 2.5.9 2.5 2-1.1 2-2.5 2A2.5 2.5 0 0 1 9.5 15" />
      <path d="M12 6.5v11" />
    </svg>
  ),
  reglas: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3h11l3 3v15a0 0 0 0 1 0 0H5a0 0 0 0 1 0 0V3z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </svg>
  ),
  trading: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 14l3-4 3 2 4-6" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
  collapse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  ),
  more: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
};

// Rutas que viven en el bottom-nav móvil; el resto va al panel "Más".
const MOBILE_TAB_ROUTES = ['/', '/calendar', '/trades', '/news'];

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', exact: true, icon: ICON.dashboard },
  { to: '/calendar', label: 'Calendario', icon: ICON.calendar },
  { to: '/trades', label: 'Trades', icon: ICON.trades },
  { to: '/portfolio', label: 'Portafolio', icon: ICON.portfolio },
  { to: '/news', label: 'Brief & news', icon: ICON.news },
  { to: '/payouts', label: 'Payouts', icon: ICON.payouts },
  { to: '/accounts', label: 'Cuentas', icon: ICON.accounts },
  { to: '/settings', label: 'Settings', icon: ICON.settings },
];

// Secciones personales de Maxi (bitácora + playbook) — solo el admin las ve;
// los demás usuarios ni siquiera las tienen en el nav (y la ruta los redirige).
const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/bitacora', label: 'Bitácora', icon: ICON.bitacora },
  { to: '/estrategia', label: 'Mi Estrategia', icon: ICON.estrategia },
  { to: '/admin', label: 'Admin', icon: ICON.admin },
];

// Espacio FONDO CRIPTO (Plutus) — fondo común Maxi+Nelson, admin-only. Separado
// por construcción del trading personal: su propio nav, sus propias páginas
// /fondo/*. El selector de espacio alterna entre "Mi Trading" y este.
const FONDO_NAV_ITEMS: NavItem[] = [
  { to: '/fondo/bitacora', label: 'Bitácora', icon: ICON.bitacora },
  { to: '/fondo/reglas', label: 'Estilo & Reglas', icon: ICON.reglas },
];

// Selector de ESPACIO — "Mi Trading" (Njord) vs "Fondo Cripto" (Plutus). Solo
// admin. El muro visual para no confundir los dos espacios.
function SpaceSwitcher({
  inFondo,
  collapsed,
  onPick,
}: {
  inFondo: boolean;
  collapsed?: boolean;
  onPick: (space: 'trading' | 'fondo') => void;
}): ReactElement {
  if (collapsed) {
    return (
      <div className="px-2 pt-3 flex flex-col gap-1">
        <button
          onClick={() => onPick('trading')}
          title="Mi Trading"
          className={cn(
            'flex items-center justify-center h-9 rounded-lg transition-colors',
            !inFondo
              ? 'bg-accent-500/12 text-accent-500'
              : 'text-ink-subtle hover:text-ink hover:bg-surface-panel/70',
          )}
        >
          <span className="w-5 h-5">{ICON.trading}</span>
        </button>
        <button
          onClick={() => onPick('fondo')}
          title="Fondo Cripto"
          className={cn(
            'flex items-center justify-center h-9 rounded-lg transition-colors',
            inFondo
              ? 'bg-accent-500/12 text-accent-500'
              : 'text-ink-subtle hover:text-ink hover:bg-surface-panel/70',
          )}
        >
          <span className="w-5 h-5">{ICON.coin}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="px-3 pt-3">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-surface-panel/60 border border-surface-border">
        <button
          onClick={() => onPick('trading')}
          className={cn(
            'flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-semibold transition-colors',
            !inFondo ? 'bg-surface-elevated text-ink shadow-sm' : 'text-ink-subtle hover:text-ink',
          )}
        >
          <span className="w-4 h-4">{ICON.trading}</span> Mi Trading
        </button>
        <button
          onClick={() => onPick('fondo')}
          className={cn(
            'flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-semibold transition-colors',
            inFondo ? 'bg-accent-500/15 text-accent-500 shadow-sm' : 'text-ink-subtle hover:text-ink',
          )}
        >
          <span className="w-4 h-4">{ICON.coin}</span> Fondo
        </button>
      </div>
    </div>
  );
}

export function AppLayout({ children }: Props): ReactElement {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem('bini.sidebar.collapsed') === '1',
  );
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  // El espacio activo se deriva de la URL (no de un estado aparte): así un deep
  // link a /fondo/* abre el espacio del fondo sin desincronizarse.
  const inFondo = pathname.startsWith('/fondo');

  // Nav según el espacio. El fondo es admin-only; los no-admin solo ven trading.
  const navItems = !isAdmin
    ? NAV_ITEMS
    : inFondo
      ? FONDO_NAV_ITEMS
      : [...NAV_ITEMS, ...ADMIN_NAV_ITEMS];

  // Items del bottom-nav móvil y los que quedan para el panel "Más".
  // En el fondo (pocos items) van todos al bottom-nav.
  const mobileTabs = inFondo
    ? navItems
    : navItems.filter((i) => MOBILE_TAB_ROUTES.includes(i.to));
  const moreItems = inFondo
    ? []
    : navItems.filter((i) => !MOBILE_TAB_ROUTES.includes(i.to));

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      localStorage.setItem('bini.sidebar.collapsed', next ? '1' : '0');
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-surface-base text-ink flex flex-col md:flex-row">
      {/* ──── Sidebar (desktop) ──── */}
      <aside
        className={cn(
          'hidden md:flex flex-col shrink-0 sticky top-0 h-screen',
          'border-r border-surface-border bg-surface-elevated/60 backdrop-blur-md',
          'transition-[width] duration-200 ease-out',
          collapsed ? 'w-[72px]' : 'w-[232px]',
        )}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Brand */}
        <button
          onClick={() => navigate('/')}
          className={cn(
            'flex items-center gap-3 h-16 px-4 group',
            'border-b border-surface-border/60',
            collapsed && 'justify-center px-0',
          )}
          aria-label="Ir al dashboard"
        >
          <BiniLogo size={32} showWordmark={!collapsed} />
        </button>

        {/* Selector de espacio (admin): Mi Trading ⇄ Fondo Cripto */}
        {isAdmin && (
          <SpaceSwitcher
            inFondo={inFondo}
            collapsed={collapsed}
            onPick={(space) => navigate(space === 'fondo' ? '/fondo/bitacora' : '/')}
          />
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 h-10 rounded-lg text-sm font-medium transition-all',
                  collapsed ? 'justify-center px-0' : 'px-3',
                  isActive
                    ? 'bg-accent-500/12 text-accent-500 shadow-[inset_3px_0_0_0_currentColor]'
                    : 'text-ink-muted hover:text-ink hover:bg-surface-panel/70',
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <span className="w-5 h-5 shrink-0">{item.icon}</span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-surface-border/60 p-3 space-y-2">
          {!collapsed && user && (
            <div className="px-2 py-2 rounded-lg bg-surface-panel/50">
              <div className="text-xs font-semibold text-ink truncate">{user.name}</div>
              <div className="text-[10px] text-ink-subtle truncate">{user.email}</div>
            </div>
          )}
          <div className={cn('flex items-center gap-1.5', collapsed && 'flex-col')}>
            <ThemeToggle />
            <button
              onClick={logout}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'w-9 h-9 rounded-full border border-surface-border',
                'bg-surface-elevated hover:bg-loss-DEFAULT/10 hover:border-loss-DEFAULT/40 hover:text-loss-DEFAULT',
                'text-ink-muted transition-all duration-200',
              )}
              title="Salir"
              aria-label="Cerrar sesión"
            >
              <span className="w-4 h-4">{ICON.logout}</span>
            </button>
            <button
              onClick={toggleCollapsed}
              className={cn(
                'inline-flex items-center justify-center',
                'w-9 h-9 rounded-full border border-surface-border',
                'bg-surface-elevated hover:bg-surface-panel',
                'text-ink-muted hover:text-ink transition-all duration-200',
                collapsed && 'rotate-180',
              )}
              title={collapsed ? 'Expandir' : 'Colapsar'}
              aria-label="Colapsar sidebar"
            >
              <span className="w-4 h-4">{ICON.collapse}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ──── Mobile topbar ──── */}
      {/* min-height + padding (no altura fija): el inset de la barra de estado
          se suma ARRIBA del bar en vez de comerse su alto, así el logo nunca
          queda bajo la hora del iPhone (PWA black-translucent). */}
      <header
        className="md:hidden sticky top-0 z-30 border-b border-surface-border bg-surface-elevated/85 backdrop-blur-md flex items-center justify-between px-4"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)',
          minHeight: 'calc(3.5rem + env(safe-area-inset-top))',
        }}
      >
        <button onClick={() => navigate('/')} aria-label="Ir al dashboard">
          <BiniLogo size={28} showWordmark />
        </button>
        <div className="flex items-center gap-2">
          <ThemeToggle />
        </div>
      </header>

      {/* ──── Contenido ──── */}
      <main className="flex-1 min-w-0 pb-24 md:pb-0">{children}</main>

      {/* ──── Mobile bottom tabs (4 principales + "Más") ──── */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-surface-border bg-surface-elevated/95 backdrop-blur-md flex items-stretch px-1"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {mobileTabs.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[10px] font-medium transition-colors',
                isActive ? 'text-accent-500' : 'text-ink-subtle hover:text-ink',
              )
            }
          >
            <span className="w-5 h-5">{item.icon}</span>
            <span className="leading-none truncate">{item.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Más opciones"
          className={cn(
            'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[10px] font-medium transition-colors',
            menuOpen ? 'text-accent-500' : 'text-ink-subtle hover:text-ink',
          )}
        >
          <span className="w-5 h-5">{ICON.more}</span>
          <span className="leading-none truncate">Más</span>
        </button>
      </nav>

      {/* ──── Mobile "Más" sheet ──── */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          />
          <div
            className="absolute bottom-0 inset-x-0 rounded-t-2xl border-t border-surface-border bg-surface-elevated shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.6)]"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <span className="text-sm font-semibold text-ink">Menú</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Cerrar"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full text-ink-muted hover:text-ink hover:bg-surface-panel/70"
              >
                <span className="w-4 h-4">{ICON.close}</span>
              </button>
            </div>

            {isAdmin && (
              <div className="px-2 pb-1">
                <SpaceSwitcher
                  inFondo={inFondo}
                  onPick={(space) => {
                    setMenuOpen(false);
                    navigate(space === 'fondo' ? '/fondo/bitacora' : '/');
                  }}
                />
              </div>
            )}

            <nav className="px-3 pb-2 grid grid-cols-2 gap-2">
              {moreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 h-12 px-3 rounded-xl text-sm font-medium transition-all',
                      isActive
                        ? 'bg-accent-500/12 text-accent-500'
                        : 'text-ink-muted hover:text-ink bg-surface-panel/50 hover:bg-surface-panel',
                    )
                  }
                >
                  <span className="w-5 h-5 shrink-0">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="mx-3 mt-2 pt-3 border-t border-surface-border/60 flex items-center gap-3 px-2 pb-1">
              {user && (
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-ink truncate">{user.name}</div>
                  <div className="text-[10px] text-ink-subtle truncate">{user.email}</div>
                </div>
              )}
              <ThemeToggle />
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                aria-label="Cerrar sesión"
                className={cn(
                  'inline-flex items-center justify-center w-9 h-9 rounded-full border border-surface-border',
                  'bg-surface-elevated hover:bg-loss-DEFAULT/10 hover:border-loss-DEFAULT/40 hover:text-loss-DEFAULT',
                  'text-ink-muted transition-all duration-200',
                )}
                title="Salir"
              >
                <span className="w-4 h-4">{ICON.logout}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
